import { FormSplitOption } from './generateSplitOptions';

/**
 * Metadata for a split option, stored separately from Select options to avoid
 * custom props being spread to DOM elements.
 */
export interface SplitOptionMetadata {
    recordCount?: number;
}

/**
 * Represents a properly formatted Select option for Ant Design (standard fields only)
 */
export interface FormattedSplitOption {
    value?: string;
    label: string;
    options?: Array<{
        value: string;
        label: string;
    }>;
}

/**
 * Return type for formatSplitOptionsForUI containing both standard options and metadata
 */
export interface FormattedSplitOptionsResult {
    /** Standard options for Ant Design Select (no custom props) */
    options: FormattedSplitOption[];
    /** Metadata map keyed by option value for accessing recordCount etc. */
    metadata: Map<string, SplitOptionMetadata>;
}

/**
 * Formats split options for UI consumption by creating proper Ant Design Select option groups.
 *
 * Takes the raw split options and organizes them into proper option groups with
 * separators and formatting for the select dropdown component. Creates logical
 * groupings of no-split and show-value (filter) options with appropriate
 * group headers and dividers.
 *
 * Returns standard options (value, label only) plus a separate metadata map
 * for custom properties like recordCount to avoid React DOM warnings.
 * 
 * @param splitOptions - Raw split options from generateSplitOptions
 * @returns Object with standard options and metadata map
 */
export const formatSplitOptionsForUI = (splitOptions: FormSplitOption[]): FormattedSplitOptionsResult => {
    const groups: { [key: string]: Array<{ value: string; label: string }> } = {
        'no-split': [],
        'show-value': []
    };
    const metadata = new Map<string, SplitOptionMetadata>();

    splitOptions.forEach(option => {
        groups[option.mode].push({
            value: option.value,
            label: option.label
        });
        metadata.set(option.value, { recordCount: option.recordCount });
    });

    const selectOptions: FormattedSplitOption[] = [];

    // Add no-split options directly (no group needed for single "No split" option)
    if (groups['no-split'].length > 0) {
        selectOptions.push(...groups['no-split']);
    }

    // Add show-value options as a group if they exist
    if (groups['show-value'].length > 0) {
        selectOptions.push({
            label: 'Filter to',
            options: groups['show-value']
        });
    }

    return { options: selectOptions, metadata };
};